import React from 'react';
import { Modal, Form, Input, Select, Slider, ColorPicker } from 'antd';
import type { Color } from 'antd/es/color-picker';
import { useSessionData } from '../../hooks/useSessionData';
import type { TerrainType, RegionFormData } from '../../types/types';
import { logger } from '../../services/logger.client';

interface CreateRegionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: RegionFormData) => void;
}

const CreateRegionModal: React.FC<CreateRegionModalProps> = ({ isOpen, onClose, onSave }) => {
  const [form] = Form.useForm();
  const terrainTypes = useSessionData(session => session.cache.terrainTypes, []);

  const handleOk = () => {
    form
      .validateFields()
      .then(values => {
        onSave({
          name: values.name,
          color: typeof values.color === 'string' ? values.color : (values.color as Color)?.toHexString(),
          opacity: values.opacity / 100, // Convert from 0-100 to 0-1
          terrainTypeId: values.terrainTypeId ?? null,
        });
        onClose();
      })
      .catch(info => {
        logger.warn({ formValues: info.values, errorFields: info.errorFields }, 'Create Region validation failed.');
      });
  };

  return (
    <Modal
      title="Create New Region"
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      okText="Save Region"
      style={{ position: 'fixed', bottom: 20, left: 20, top: 'auto', margin: 0 }}
      destroyOnHidden
      // By using afterClose, we ensure the form is reset after the modal is gone.
      // And initialValues handles the defaults on mount.
      afterClose={() => form.resetFields()}
    >      
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} name="create_region_form" autoComplete="off" initialValues={{
        opacity: 20, color: '#9c27b0'
      }}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a name for the region.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="color" label="Color">
          <ColorPicker showText style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="opacity" label="Opacity">
          <Slider min={0} max={100} step={5} />
        </Form.Item>
        <Form.Item name="terrainTypeId" label="Terrain">
          <Select placeholder="-- None --" allowClear>
            {((terrainTypes || []) ?? []).map((terrain: TerrainType) => (
              <Select.Option key={terrain.id} value={terrain.id}>
                {terrain.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateRegionModal;